import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { useTheme } from '@material-ui/core/styles';

import { setPortal } from 'modules/portal';
import { makeRowColumn, getHeadObj, getDate } from 'lib/commonLib';
import { budgetStatistics, budgetGraphData } from 'lib/api/report';
import { paramConverter } from 'modules/common';
import { userOnLogin } from 'lib/api/user';

import NewButton from 'components/molecules/NewButton';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import RemainingAmountFilter from 'components/molecules/RemainingAmountFilter';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import NewTableContainer from 'components/molecules/NewTable';
import SnackbarWrap from 'components/organisms/Snackbar/SnackbarWrap';

import BudgetGraph from './BudgetGraph';

const Budget = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));
  //   const { URL, ADVER_ID } = data;
  // 공통 필터에서 초기값 받아왔는지 확인
  const [initialization, setInitialization] = useState(false);

  const initParams = {
    sort: 'desc',
    order: 'STATS_DTTM',
    sDate: getDate(moment(moment().format('YYYY-MM-01'))),
    eDate: getDate(moment()),
    contact: 'all',
    pltfomTpCode: 'all',
    itlTpCode: 'all',
  };
  const [params, setParams] = useState(initParams);
  const [graphData, setGraphData] = useState([]);
  const [graphStatus, setGraphStatus] = useState('loading');
  const [graphSumCount, setGraphSumCount] = useState([]);
  const initSnackbar = {
    open: false,
    variant: '',
    message: '',
  };
  const [snackbar, setSnackbar] = useState(initSnackbar);

  const graphList = [
    {
      title: t('일별'),
      data: { bar: ['CHARGE_POINT'] },
      yAxisData: {
        left: { CHARGE_POINT: true },
        right: {},
      },
      referenceLine: {
        CHARGE_POINT: {
          type: 'average',
          label: t('지출금액'),
          visibleValue: true,
        },
      },
    },
    {
      title: t('월별'),
      data: { bar: ['CHARGE_POINT'] },
      yAxisData: {
        left: { CHARGE_POINT: true },
        right: {},
      },
      referenceLine: {
        CHARGE_POINT: {
          type: 'average',
          label: t('지출금액'),
          visibleValue: true,
        },
      },
    },
  ];

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  useEffect(() => {
    budgetGraphData(paramConverter(params))
      .then((response) => {
        const { data } = response.data;
        if (data.dayData.length <= 0 && data.monthData.length <= 0) {
          setGraphStatus('noData');
        } else {
          setGraphData([data.dayData, data.monthData]);
          const newSumCount = [data.dayTotalPoint, data.monthTotalPoint];
          setGraphSumCount(newSumCount);
          setGraphStatus('');
        }
      })
      .catch((error) => {
        setGraphStatus('error');
        setSnackbar({
          open: true,
          variant: 'error',
          message: error,
        });
      });
  }, [params]);

  // header 및 body 컬럼 커스텀
  const customized = {
    CORP_NAME: {
      makeBody: (rowObj, headsMap) => {
        const adverId = getHeadObj(headsMap, 'ADVER_ID');
        let resultComponent;
        const showWindowUrl =
          '/report/daily/par?adverId=' +
          rowObj['ADVER_ID'] +
          '&sDate=' +
          params.sDate +
          '&eDate=' +
          params.eDate +
          '&uri=/report/daily/par&isPop=1&popGroup=adverStatistics';
        const subTitle = rowObj['PLTFOM_TP_CODE'];

        if (rowObj['CONTACT_NAME'] !== 'total') {
          resultComponent = (
            <IconAreaWrap
              text={makeRowColumn(
                rowObj['CORP_NAME'],
                adverId.type,
                adverId.format
              )}
            >
              <NewButton
                title={t('해당 광고주의 상세 통계')}
                iconBtn
                icon="faExternalLink"
                onClick={(e) => handlePortalUrl(showWindowUrl)}
              />
              <NewButton
                title={t('광고주 로그인')}
                icon="faSignInAlt"
                iconBtn
                onClick={(e) => handleOnLogin(rowObj['ADVER_ID'])}
              />
              <NewButton
                title={t('사이트로 이동')}
                icon="faHome"
                iconBtn
                onClick={(e) => handlePortalUrl('http://' + rowObj['URL'])}
              />
            </IconAreaWrap>
          );
        } else if (subTitle === '01' || subTitle === '02') {
          let tempCode;
          if (subTitle === '01') {
            tempCode = '웹';
          } else if (subTitle === '02') {
            tempCode = '모바일';
          }
          resultComponent = <>{tempCode}</>;
        }

        return resultComponent;
      },
    },
    DAY_USED_BDGT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'DAY_USED_BDGT');
        return (
          <p style={{ color: theme.palette.common.red }}>
            {makeRowColumn(
              rowObj['DAY_USED_BDGT'],
              bodyItem1.type,
              bodyItem1.format
            )}
          </p>
        );
      },
    },
    YESTER_ADVRTS_AMT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'YESTER_ADVRTS_AMT');
        return (
          <p style={{ color: theme.palette.common.blue }}>
            {makeRowColumn(
              rowObj['YESTER_ADVRTS_AMT'],
              bodyItem1.type,
              bodyItem1.format
            )}
          </p>
        );
      },
    },
    URL: {
      merged: true,
    },
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    setInitialization(true);
    setParams(initParams);
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  const handleOnchangeFilter = (filterParam) => {
    setParams({ ...params, ...filterParam });
  };

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = {
      url: url,
      cnt: popIndex,
    };

    dispatch(setPortal(newPortal));
  };

  /**
   * 로그인처리
   */
  const handleOnLogin = (ADVER_ID) => {
    userOnLogin(ADVER_ID, 'manage')
      .then((response) => {
        const { data, success } = response.data;
        if (success) {
          handlePortalUrl(data.url);
        } else {
          alert('로그인에 실패했습니다. 관리자에게 문의하세요.');
        }
      })
      .catch((error) => {
        console.log('error', error);
      });
  };

  /**
   * 스낵바 닫는 함수
   * 연속 클릭이 들어왔을 때는 기존 스낵바를 유지
   * @param event
   * @param reason
   */
  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({
      ...snackbar,
      open: false,
      message: '',
    });
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          useManagerFilter
        />
        <RemainingAmountFilter
          initialization={initialization}
          handleOnchangeFilter={handleOnchangeFilter}
          label={t('일사용예산')}
          sPointName="sPoint"
          ePointName="ePoint"
        />
      </NewFilterWrap>
      <BudgetGraph
        params={params}
        graphList={graphList}
        title={t('일별/월별 예산 그래프')}
        barData={graphData}
        status={graphStatus}
        sumCount={{
          data: graphSumCount,
          label: ['총 지출금액', '총 지출금액'],
        }}
        labelType="DD"
      />

      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={budgetStatistics}
        customized={customized}
      />
      <SnackbarWrap
        open={snackbar.open}
        onClose={handleSnackBarClose}
        variant={snackbar.variant}
        message={snackbar.message}
      />
    </>
  );
};

export default Budget;
